/**
 * Card Completion Middleware
 *
 * RTK listener middleware that detects when an AI fix completes (or fails)
 * and transitions the corresponding card from 'updating' to 'completed' or 'idle'.
 *
 * Why middleware instead of a hook:
 * SidebarContainer conditionally renders only one mode (chat/audit/analysis).
 * When handleFixWithAI switches to chat mode, the audit content unmounts —
 * any hook in RulesAtRiskView would stop running. This middleware runs at the
 * Redux store level, independent of component mount state.
 *
 * Pattern follows: features/form-builder/store/autosaveMiddleware.ts
 */

import { clearInterrupt, completeWorkflow, setError, showInterrupt } from '@features/note-ai-edit/store';
import type { InterruptState } from '@features/note-ai-edit/store/chatSlice';
import { clearSectionSnapshot } from '@features/note-ai-edit/store/chatSlice';
import { createListenerMiddleware } from '@reduxjs/toolkit';

import type { AuditUiState } from './auditUiSlice';
import {
  completeCardUpdate,
  failCardUpdate,
  resumeCardUpdate,
  setCardInterrupted,
  startCardUpdate,
} from './auditUiSlice';

interface NoteAiEditSliceState {
  affectedSectionIds: string[];
  sectionPreviousContent: Record<string, string>;
  interrupt: InterruptState | null;
  messages: Array<{ id: string; role: string; content: string }>;
}

// Dynamic feature slices are not on the static RootState type.
// Use a loose record type consistent with how selectors access them.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DynamicState = Record<string, any>;

export const cardCompletionMiddleware = createListenerMiddleware();

/**
 * When the AI workflow completes successfully, transition the updating card to 'completed'.
 * Diffs affectedSectionIds (before vs after) to determine which sections this fix touched.
 */
cardCompletionMiddleware.startListening({
  actionCreator: completeWorkflow,
  effect: (_action, listenerApi) => {
    const state = listenerApi.getState() as DynamicState;
    const uiState = state['jotaudit/ui'] as AuditUiState | undefined;
    const updatingRuleId = uiState?.updatingRuleId;
    if (!updatingRuleId) return;

    const noteAiState = state['noteAiEdit/ui'] as NoteAiEditSliceState | undefined;

    // Use sectionPreviousContent to find sections the AI edited.
    // Diff snapshot keys (now vs before fix) to isolate this fix's sections.
    // The old approach (diffing affectedSectionIds) failed when the AI re-edited
    // a section already in the global affected list from a prior fix.
    const snapshots = noteAiState?.sectionPreviousContent ?? {};
    const preFixSnapshots: string[] = uiState?.preFixSnapshotKeys ?? [];
    const newSectionIds = Object.keys(snapshots).filter(id => !preFixSnapshots.includes(id));

    // Extract the AI's change summary from the last assistant message
    const messages = noteAiState?.messages ?? [];
    const lastAssistantMsg = [...messages].reverse().find(m => m.role === 'assistant');

    listenerApi.dispatch(
      completeCardUpdate({
        affectedSectionIds: newSectionIds,
        changeSummary: lastAssistantMsg?.content,
      }),
    );
  },
});

/**
 * When the AI workflow errors, reset the card back to idle.
 */
cardCompletionMiddleware.startListening({
  actionCreator: setError,
  effect: (_action, listenerApi) => {
    const state = listenerApi.getState() as DynamicState;
    const uiState = state['jotaudit/ui'] as AuditUiState | undefined;
    if (!uiState?.updatingRuleId) return;

    listenerApi.dispatch(failCardUpdate());
  },
});

/**
 * When an AI interrupt arrives while a card is updating, transition to 'interrupted'.
 * Skip condition mirrors useAutoApproveInterrupt exactly: note_changes + 1 section.
 */
cardCompletionMiddleware.startListening({
  actionCreator: showInterrupt,
  effect: (_action, listenerApi) => {
    const state = listenerApi.getState() as DynamicState;
    const uiState = state['jotaudit/ui'] as AuditUiState | undefined;
    const updatingRuleId = uiState?.updatingRuleId;
    if (!updatingRuleId) return;

    // Only transition if the card is still in 'updating' state
    if (uiState.cardStates[updatingRuleId]?.status !== 'updating') return;

    const noteAiState = state['noteAiEdit/ui'] as NoteAiEditSliceState | undefined;
    const interrupt = noteAiState?.interrupt;
    if (!interrupt?.isActive) return;

    // Skip condition must EXACTLY mirror useAutoApproveInterrupt's guard.
    // The auto-approve hook handles these — don't transition the card.
    const affectedSectionIds: string[] = noteAiState?.affectedSectionIds ?? [];
    const isAutoApprovable =
      interrupt.eventType === 'CONFIRMATION' &&
      (!interrupt.namespace || interrupt.namespace === 'global') &&
      interrupt.confirmationType === 'note_changes' &&
      affectedSectionIds.length === 1;

    if (isAutoApprovable) return;

    listenerApi.dispatch(setCardInterrupted(updatingRuleId));
  },
});

/**
 * When an interrupt clears (user responded or auto-approved), resume the card update.
 */
cardCompletionMiddleware.startListening({
  actionCreator: clearInterrupt,
  effect: (_action, listenerApi) => {
    const state = listenerApi.getState() as DynamicState;
    const uiState = state['jotaudit/ui'] as AuditUiState | undefined;
    const updatingRuleId = uiState?.updatingRuleId;
    if (!updatingRuleId) return;

    if (uiState.cardStates[updatingRuleId]?.status === 'interrupted') {
      listenerApi.dispatch(resumeCardUpdate(updatingRuleId));
    }
  },
});

/**
 * When a new card update starts, clear section snapshots for cards that just graduated.
 * This scopes undo to only the latest fix — graduated cards can no longer be reverted.
 */
cardCompletionMiddleware.startListening({
  actionCreator: startCardUpdate,
  effect: (_action, listenerApi) => {
    const state = listenerApi.getState() as DynamicState;
    const uiState = state['jotaudit/ui'] as AuditUiState | undefined;
    if (!uiState) return;

    const graduatedSectionIds: string[] = [];
    for (const card of Object.values(uiState.cardStates)) {
      if ((card.status === 'completed' || card.status === 'ignored') && card.graduated) {
        graduatedSectionIds.push(...card.affectedSectionIds);
      }
    }

    for (const sectionId of graduatedSectionIds) {
      listenerApi.dispatch(clearSectionSnapshot(sectionId));
    }
  },
});
